import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';
import { IGMBUser } from '../shared/interfaces/IGMBUser';
import http from '../shared/HttpCommon';
import { CanceledError } from 'axios';
import { CodeResponse, useGoogleLogin } from '@react-oauth/google';
import { AuthContext } from './AuthProvider';

interface Props {
  children?: ReactNode;
}

interface GMBAuthContextProps {
  user?: IGMBUser;
  login: () => void;
  logout: () => Promise<void>;
}

export const GMBAuthContext = createContext({} as GMBAuthContextProps);

const GMBAuthProvider: FC<Props> = ({ children }) => {
  const [user, setUser] = useState<IGMBUser>();
  const { authenticated } = useContext(AuthContext);

  const login = useGoogleLogin({
    onSuccess: (codeResponse) => handleSuccess(codeResponse),
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/business.manage',
  });

  const handleSuccess = (codeResponse: CodeResponse) => {
    const exchangeCodeForToken = async () => {
      return await http.post('/google/auth/token', {
        code: codeResponse.code,
      });
    };
    exchangeCodeForToken().then((response) => {
      setUser(response.data);
    });
  };

  const logout = async () => {
    if (!user) return;

    try {
      const { data } = await http.post('/google/auth/revoke', {
        id: user.id,
      });

      if (data.success === true) {
        setUser(undefined);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updateUserInfo = async (data: IGMBUser) => {
    if (!data) return;

    try {
      if (!data.isLoggedIn) {
        await http.post('/google/auth/delete', {
          id: data.id,
        });
        window.location.reload();
      } else {
        setUser(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    if (authenticated) {
      (async () => {
        try {
          const { data } = await http.get('/google/auth/userinfo', { signal: controller.signal });
          await updateUserInfo(data);
        } catch (error) {
          if (error instanceof CanceledError) {
          } else {
            console.error(error);
          }
        }
      })();
    }

    return () => {
      controller.abort();
    };
  }, [authenticated]);

  return (
    <GMBAuthContext.Provider value={{ user, login, logout }}>{children}</GMBAuthContext.Provider>
  );
};

export default GMBAuthProvider;
